import type { TuplePrefix } from '../../lib/util';
import type { MultiKeyJSON } from './ExtendedJSON';

export type Narrowable =
  | Record<string, unknown>
  | undefined
  | boolean
  | string
  | number
  | symbol
  | bigint
  | Date
  | void
  | null
  | [];

export type JSNode<KeyType extends Narrowable[], ValueType> =
  | [KeyType[number], JSNode<KeyType, ValueType>][]
  | ValueType;

type MKMap<KeyType extends Narrowable[], ValueType> = Map<
  KeyType[number],
  MKMap<KeyType, ValueType> | ValueType
>;

function set<KeyType extends Narrowable[], ValueType>(
  map: MKMap<KeyType, ValueType>,
  value: ValueType,
  keys: KeyType,
): void {
  const last = keys.length - 1;
  for (let i = 0; i < last; ++i) {
    const key = keys[i];
    let tmp = map.get(key) as MKMap<KeyType, ValueType> | undefined;
    if (tmp === undefined) {
      tmp = new Map();
      map.set(key, tmp);
    }
    map = tmp;
  }
  map.set(keys[last], value);
}

function get<KeyType extends Narrowable[], ValueType>(
  map: MKMap<KeyType, ValueType>,
  keys: KeyType,
): ValueType | undefined;
// noinspection JSUnusedLocalSymbols
function get<KeyType extends Narrowable[], ValueType>(
  map: MKMap<KeyType, ValueType>,
  keys: TuplePrefix<KeyType>,
): MKMap<KeyType, ValueType> | undefined;
function get<KeyType extends Narrowable[], ValueType>(
  map: MKMap<KeyType, ValueType>,
  keys: KeyType | TuplePrefix<KeyType>,
): MKMap<KeyType, ValueType> | ValueType | undefined {
  const length = keys.length;
  for (let i = 0; i < length; ++i) {
    const key = keys[i] as KeyType[number];
    let nextMap = map.get(key) as MKMap<KeyType, ValueType> | undefined;
    // When JSON deserializing references, the path key may be a numeric string
    if (nextMap === undefined && typeof key === 'string') {
      const intKey = parseInt(key) as KeyType[number];
      nextMap = map.get(intKey) as MKMap<KeyType, ValueType> | undefined;
    }
    if (nextMap === undefined) {
      return undefined;
    }
    map = nextMap;
  }
  return map;
}

function forEachFast<KeyType extends Narrowable[], ValueType>(
  map: MKMap<KeyType, ValueType>,
  fn: (v: ValueType) => void,
  index: number,
  last: number,
): void {
  if (index < last) {
    const nextIndex = index + 1;
    for (const value of map.values()) {
      forEachFast(value as MKMap<KeyType, ValueType>, fn, nextIndex, last);
    }
  } else {
    for (const value of (map as Map<KeyType[number], ValueType>).values()) {
      fn(value);
    }
  }
}

function getSubTreeValues<KeyType extends Narrowable[], ValueType>(
  map: MKMap<KeyType, ValueType>,
  keys: TuplePrefix<KeyType>,
  keyLength: number,
): ValueType[] {
  const result: ValueType[] = [];
  const remains = keyLength - keys.length;
  if (!(remains > 0)) {
    return result;
  }
  const subMap = get(map, keys);
  if (!subMap) {
    return result;
  }
  forEachFast(subMap, (value) => result.push(value), 0, remains - 1);
  return result;
}

function clone<KeyType extends Narrowable[], ValueType>(
  source: MKMap<KeyType, ValueType>,
  target: MKMap<KeyType, ValueType>,
  index: number,
  last: number,
): void {
  const nextIndex = index + 1;
  if (nextIndex !== last) {
    for (const [key, value] of source) {
      const nextTarget = new Map();
      target.set(key, nextTarget);
      clone(value as MKMap<KeyType, ValueType>, nextTarget, nextIndex, last);
    }
  } else {
    for (const [key, value] of source) {
      const nextTarget = new Map(value as Map<KeyType[number], ValueType>);
      target.set(key, nextTarget);
    }
  }
}

function toJS<KeyType extends Narrowable[], ValueType>(
  map: MKMap<KeyType, ValueType>,
  index: number,
  last: number,
): [KeyType[number], JSNode<KeyType, ValueType>][] {
  const isLast = index === last;
  if (isLast) {
    return Array.from(map as Map<KeyType[number], ValueType>);
  }

  const nextIndex = index + 1;
  const result: [Narrowable, JSNode<KeyType, ValueType>][] = [];
  for (const [key, value] of map) {
    result.push([
      key,
      toJS(value as MKMap<KeyType, ValueType>, nextIndex, last),
    ]);
  }

  return result;
}

function fromJS<KeyType extends Narrowable[], ValueType>(
  obj: [Narrowable, JSNode<KeyType, ValueType>][],
  target: MKMap<KeyType, ValueType>,
  index: number,
  last: number,
): void {
  const nextIndex = index + 1;
  if (nextIndex < last) {
    for (const [key, value] of obj) {
      const nextTarget = new Map();
      target.set(key, nextTarget);
      fromJS(
        value as [Narrowable, JSNode<KeyType, ValueType>][],
        nextTarget,
        nextIndex,
        last,
      );
    }
  } else {
    for (const [key, value] of obj) {
      const nextTarget = new Map(value as [KeyType[number], ValueType][]);
      target.set(key, nextTarget);
    }
  }
}

function forEach<KeyType extends Narrowable[], ValueType>(
  map: MKMap<KeyType, ValueType>,
  fn: (v: ValueType, path: KeyType) => void,
  path: KeyType[number][] = [],
  last: number,
): void {
  if (path.length < last) {
    for (const [key, value] of map) {
      forEach(value as MKMap<KeyType, ValueType>, fn, path.concat(key), last);
    }
  } else {
    for (const [key, value] of map) {
      fn(value as ValueType, path.concat(key) as KeyType);
    }
  }
}

export class MultiKey<ValueType, KeyType extends Narrowable[]> {
  map: MKMap<KeyType, ValueType>;
  readonly keyLength: number;

  constructor(keyLength: number, multiKey?: MultiKey<ValueType, KeyType>) {
    this.keyLength = keyLength;
    this.map =
      multiKey instanceof MultiKey
        ? multiKey.map
        : new Map<KeyType[0], MKMap<KeyType, ValueType>>();
  }

  set(value: ValueType, ...keys: KeyType): void {
    set(this.map, value, keys);
  }

  get(...keys: KeyType): ValueType | undefined {
    return get(this.map, keys);
  }

  update(
    fn: (oldValue: ValueType | undefined) => ValueType,
    ...keys: KeyType
  ): void {
    const map = this.map;
    const oldValue = get(map, keys);
    const newValue = fn(oldValue);
    set(map, newValue, keys);
  }

  /** Get all the values contained in the subtree specified by the prefix of the full key */
  getSubTreeValues(...keys: TuplePrefix<KeyType>): ValueType[] {
    return getSubTreeValues(this.map, keys, this.keyLength);
  }

  clone(): MultiKey<ValueType, KeyType> {
    const keyLength = this.keyLength;
    const result = new MultiKey<ValueType, KeyType>(keyLength);
    clone(this.map, result.map, 0, keyLength - 1);
    return result;
  }

  toJS(): MultiKeyJSON<KeyType, ValueType> {
    const keyLength = this.keyLength;
    const value = toJS(this.map, 0, keyLength - 1);
    return {
      __type: 'MultiKey',
      keyLength: keyLength,
      value: value,
    };
  }

  /** Turns a nested JS object into a MultiKey. It's the responsibility of the caller to ensure the values are of correct type. */
  static fromJS<ValueType, KeyType extends Narrowable[]>(
    js: MultiKeyJSON<KeyType, ValueType>,
  ): MultiKey<ValueType, KeyType> {
    const { value, keyLength } = js;
    const result = new MultiKey<ValueType, KeyType>(keyLength);
    if (!Array.isArray(value)) {
      return result;
    }
    fromJS(value, result.map, 0, keyLength - 1);
    return result;
  }

  /** Creates a MultiKey from an array of tuples.
   * @param tuples Array of tuples where the first element is the key tuple and the second is the value
   * @param keyLength Optional key length, defaults to the length of the first tuple
   */
  static fromTuples<ValueType, KeyType extends Narrowable[]>(
    tuples: [KeyType, ValueType][],
    keyLength?: number,
  ): MultiKey<ValueType, KeyType> {
    const result = new MultiKey<ValueType, KeyType>(
      keyLength ?? tuples[0]?.length ?? 0,
    );
    for (const [key, value] of tuples) {
      result.set(value, ...key);
    }
    return result;
  }

  forEach(fn: (v: ValueType, path: KeyType) => void): void {
    forEach(this.map, fn, [], this.keyLength - 1);
  }
}
